import { type ReactNode, useImperativeHandle, forwardRef } from "react";
import {
  useForm,
  type SubmitHandler,
  type UseFormReturn,
} from "react-hook-form";

import Cookies from "js-cookie";

type FormProps<T extends Record<string, any>> = {
  action: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  onBefore?: () => void;
  children: (form: UseFormReturn<T>) => ReactNode;
  onSuccess?: (data: unknown) => void;
  onError?: (error: unknown) => void;
  auth?: boolean;
};

export type FormHandle = {
  submit: () => void;
};

const Form = forwardRef<FormHandle, FormProps<any>>(
  (
    {
      action,
      method = "POST",
      onBefore,
      children,
      onSuccess,
      onError,
      auth = false,
    },
    ref
  ) => {
    const form = useForm();

    const handleSubmit: SubmitHandler<any> = async (values) => {
      if (onBefore) onBefore();

      try {
        // 1. Initialize headers object
        const headers: Record<string, string> = {};

        // 2. Add Content-Type for non-GET requests
        if (method !== "GET") {
          headers["Content-Type"] = "application/json";
        }

        // 3. Handle Authentication
        if (auth) {
          const token = Cookies.get("token");
          if (token) {
            headers["Authorization"] = `Token ${token}`;
          } else {
            // Optional: Handle missing token explicitly if needed
            console.warn("Auth is true, but no token was found in cookies.");
          }
        }

        const res = await fetch(action, {
          method,
          headers, // Pass the constructed headers
          body: method === "GET" ? undefined : JSON.stringify(values),
        });

        const contentType = res.headers.get("content-type");
        const data = contentType?.includes("application/json")
          ? await res.json()
          : await res.text();

        if (!res.ok || (typeof data === "object" && data?.error)) {
          throw new Error(
            typeof data === "object" && data?.error
              ? data.error
              : "Request failed"
          );
        }

        onSuccess?.(data);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === "string"
            ? err
            : err?.error ?? "An unexpected error has occured";

        onError?.(errorMessage);
      }
    };

    // Expose the submit method via the ref
    useImperativeHandle(ref, () => ({
      submit: () => form.handleSubmit(handleSubmit)(),
    }));

    return (
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="fields-container">{children(form)}</div>
      </form>
    );
  }
);

Form.displayName = "Form";

export default Form;
